import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Divider, message, Input } from "antd";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/useLanguage";
import LanguageSwitcher from "src/components/LanguageSwitcher";
import { clearLocalStorageExcept } from "src/utils/HelperFunctions";
import { useMenu } from "src/contexts/MenuContext";
import { useUser } from "src/contexts/UserContext";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { fetchAllDashboardsAPI } from "../../services/AccountDashboardAPIs";
import {
  DEFAULT_DASHBOARD_FALLBACK,
  getFirstUserAccessibleDashboard,
} from "../HomePage/SidebarManagement/DashboardRoutes";
import {
  loginUser,
  handleGoogleOAuthCallback,
  getGoogleLoginUrl,
  validateEmail,
  storeUserData,
  autoLoginLog,
} from "../../services/AuthenticationAPIs";
import { handlePushNotificationChange } from "../../services/PushNotificationHandler";

const SESSION_TIMEOUT_HOURS = 24;

/** Toast after `navigate()` so the user is not still on the login screen when it appears. */
function showLoginSuccessAfterNavigation(text: string): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      message.success(text);
    });
  });
}

/** Onboarding statuses from `tenant-login` (not yet `status: true`). */
const WELCOME_ROUTES_BY_STATUS: Record<
  number,
  { pathname: string; state: { currentStep: number } }
> = {
  2: {
    pathname: "/welcome-to-datatram/basic-information",
    state: { currentStep: 0 },
  },
  3: {
    pathname: "/welcome-to-datatram/data-loading",
    state: { currentStep: 2 },
  },
  4: {
    pathname: "/welcome-to-datatram/connect-sources",
    state: { currentStep: 1 },
  },
};

const LoginContent: React.FC = () => {
  const { t, i18n } = useTranslation();
  useLanguage(); // Initialize RTL support
  const navigate = useNavigate();
  const { setUser } = useUser();
  const { setSelectedMenuItem } = useMenu();
  const [values, setValues] = useState({ email: "", password: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");
  const hasInitialized = useRef(false);

  const navigateToFirstAccessibleDashboard = useCallback(
    async (
      rawUserId: string | number | null | undefined,
      rawApiKey: string | null | undefined,
      options: { allowFallback: boolean },
    ): Promise<boolean> => {
      const userId =
        rawUserId === null || rawUserId === undefined
          ? ""
          : String(rawUserId).trim();
      const apiKey = rawApiKey?.trim() ?? "";

      if (!userId || !apiKey) {
        if (options.allowFallback) {
          setSelectedMenuItem(DEFAULT_DASHBOARD_FALLBACK.dashboardName);
          navigate(DEFAULT_DASHBOARD_FALLBACK.route);
          return true;
        }
        return false;
      }

      try {
        const dashboards = await fetchAllDashboardsAPI(
          userId,
          apiKey,
          getApiLangFromI18n(i18n),
        );
        const first = getFirstUserAccessibleDashboard(dashboards);
        if (first) {
          setSelectedMenuItem(first.dashboardName);
          navigate(first.route);
          return true;
        }
      } catch {
        /* Dashboard list failed; fall through to allowFallback handling. */
      }

      if (options.allowFallback) {
        setSelectedMenuItem(DEFAULT_DASHBOARD_FALLBACK.dashboardName);
        navigate(DEFAULT_DASHBOARD_FALLBACK.route);
        return true;
      }
      return false;
    },
    [navigate, setSelectedMenuItem, i18n.language, i18n.resolvedLanguage],
  );

  // 🔹 Check login expiration
  useEffect(() => {
    const checkSession = () => {
      const loginTime = localStorage.getItem("login_time");
      if (loginTime) {
        const loginDate = new Date(loginTime);
        const now = new Date();
        const hoursDiff =
          (now.getTime() - loginDate.getTime()) / (1000 * 60 * 60);

        if (hoursDiff >= SESSION_TIMEOUT_HOURS) {
          clearLocalStorageExcept(["admin_token", "admin_name"]);
          localStorage.removeItem("login_time");
          message.info(t("login.sessionExpired"));
          navigate("/log-in");
        }
      }
    };

    checkSession(); // run immediately
    const interval = setInterval(checkSession, 60 * 1000); // check every 1 min

    return () => clearInterval(interval);
  }, [navigate, t]);

  // 🔹 Auto login if token already exists
  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    const init = async () => {
      const apiKey = localStorage.getItem("api_key");
      const tenantId = localStorage.getItem("tenant_id");

      if (apiKey && tenantId) {
        try {
          await autoLoginLog();
          const userId = localStorage.getItem("user_id")?.trim();

          if (userId) {
            await navigateToFirstAccessibleDashboard(userId, apiKey, {
              allowFallback: true,
            });
          } else {
            setSelectedMenuItem(DEFAULT_DASHBOARD_FALLBACK.dashboardName);
            navigate(DEFAULT_DASHBOARD_FALLBACK.route);
          }
        } catch {
          const userId = localStorage.getItem("user_id")?.trim();
          if (userId) {
            await navigateToFirstAccessibleDashboard(userId, apiKey, {
              allowFallback: true,
            });
          } else {
            setSelectedMenuItem(DEFAULT_DASHBOARD_FALLBACK.dashboardName);
            navigate(DEFAULT_DASHBOARD_FALLBACK.route);
          }
        }
      }
    };

    init();
  }, [navigate, navigateToFirstAccessibleDashboard, setSelectedMenuItem]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    const code = params.get("code");
    const state = params.get("state");

    if (code && state) {
      handleGoogleOAuthCallback(code, state)
        .then(async data => {
          if (data.status) {
            storeUserData(data, setUser);
            localStorage.setItem("login_time", new Date().toISOString());
            // Fire-and-forget push opt-in
            handlePushNotificationChange(true).catch(() => {});
            const userId =
              (data.user_id !== null && data.user_id !== undefined
                ? String(data.user_id).trim()
                : "") || localStorage.getItem("user_id")?.trim();
            const apiKey =
              (typeof data.api_key === "string" ? data.api_key.trim() : "") ||
              localStorage.getItem("api_key")?.trim();
            const successText = data.message || t("login.googleLoginSuccessful");
            if (userId && apiKey) {
              await navigateToFirstAccessibleDashboard(userId, apiKey, {
                allowFallback: true,
              });
            } else {
              setSelectedMenuItem(DEFAULT_DASHBOARD_FALLBACK.dashboardName);
              navigate(DEFAULT_DASHBOARD_FALLBACK.route);
            }
            showLoginSuccessAfterNavigation(successText);
          } else {
            message.error(data.message || t("login.googleLoginFailed"));
          }
        })
        .catch(() => {
          message.error(t("login.googleLoginError"));
        });
    }
  }, [navigate, setUser, t, navigateToFirstAccessibleDashboard, setSelectedMenuItem]);

  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    const urlMessage = params.get("message");
    if (urlMessage) {
      message.info(decodeURIComponent(urlMessage));
      params.delete("message");
      const newUrl = globalThis.location.pathname + "?" + params.toString();
      globalThis.history.replaceState({}, "", newUrl);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearLocalStorageExcept(["admin_token", "admin_name"]);

    if (!values.email?.trim() || !values.password) {
      message.error(t("login.fillEmailPassword"));
      return;
    }

    const emailValidationError = validateEmail(values.email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }
    setEmailError("");

    setIsSubmitting(true);
    try {
      const data = await loginUser(values.email, values.password);
      const successStatuses: Array<number | boolean> = [2, 3, 4, true];
      if (!successStatuses.includes(data.status)) {
        message.error(data.message || t("login.loginFailed"));
        return;
      }

      storeUserData(data, setUser);
      localStorage.setItem("login_time", new Date().toISOString());
      const successText = data.message || t("login.loginSuccessful");

      if (data.status === true) {
        // Fire-and-forget: request browser push permission and register device
        handlePushNotificationChange(true).catch(() => {});

        const roleId = data.role_id;
        if (roleId === 2) {
          const ok = await navigateToFirstAccessibleDashboard(
            data.user_id,
            data.api_key,
            { allowFallback: false },
          );
          if (!ok) {
            message.error(t("login.noDashboards"));
          } else {
            showLoginSuccessAfterNavigation(successText);
          }
          return;
        }
        if (roleId === 1) {
          await navigateToFirstAccessibleDashboard(
            data.user_id,
            data.api_key,
            { allowFallback: true },
          );
          showLoginSuccessAfterNavigation(successText);
        }
        return;
      }

      const welcomeTarget = WELCOME_ROUTES_BY_STATUS[data.status as number];
      if (welcomeTarget) {
        localStorage.setItem(
          "currentStep",
          String(welcomeTarget.state.currentStep),
        );
        navigate(welcomeTarget);
        showLoginSuccessAfterNavigation(successText);
      }
    } catch {
      message.error(t("login.somethingWentWrong"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    clearLocalStorageExcept(["admin_token", "admin_name"]);
    try {
      globalThis.location.href = getGoogleLoginUrl();
    } catch {
      message.error(t("login.failedInitiateGoogle"));
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  return (
    <div
      className="body"
      style={{ "--auth-bg-image": "url(/images/DatatramSignup.png)" } as React.CSSProperties}
    >
      <div className="logo-wrapper">
        <img src="/images/DpLogo.png" className="logo-design" alt="logo" />
        <LanguageSwitcher />
      </div>

      <div className="app">
        <div className="signup-internal-container">
          <form className="formdesign" onSubmit={handleSubmit} noValidate>
            <h2 className="signup-heading">{t("login.welcomeBack")}</h2>
            <br />

            <button
              onClick={handleGoogleLogin}
              className="custom-google-button"
              type="button"
            >
              <img
                src="/images/google-logo.svg"
                alt="Google logo"
                style={{ marginRight: "10px", width: "18px", height: "18px" }}
              />
              {t("login.loginWithGoogle")}
            </button>
            <br />
            <Divider className="or">{t("login.or")}</Divider>
            <br />

            <div className="input-container">
              <label>{t("login.emailAddress")}</label>
              <Input
                name="email"
                className="input-email-password input-style-2"
                placeholder={t("login.emailAddress")}
                value={values.email}
                onChange={onChange}
                type="email"
                required
              />
              {emailError && (
                <span className="error-message">{emailError}</span>
              )}
            </div>

            <div className="input-container">
              <label>{t("login.password")}</label>
              <Input.Password
                name="password"
                className="input-email-password input-style-2"
                placeholder={t("login.enterPassword")}
                value={values.password}
                onChange={onChange}
                required
              />
            </div>

            <p className="forgot-password-txt">
              <a href="/forgot-password">{t("login.forgotPassword")}</a>
            </p>

            <button
              type="submit"
              className="button-style"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("login.loggingIn") : t("login.logIn")}
            </button>

            <p className="signup-page-text alreadyRegistered_text">
              {t("login.dontHaveAccount")}{" "}
              <a href="/sign-up">{t("login.signUp")}</a>
            </p>

            <p className="signup-page-text viewTerms_text">
              {t("login.agreeTerms")}{" "}
              <a href="/privacy-policy">{t("login.termsAndConditions")}</a>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginContent;
